import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useDocumentSignature } from "@screens/FeaAuth/hooks/useDocumentSignature";
import type { PinState } from "@interfaces";
import { defaultPinState } from "@interfaces";
import { useParams } from "react-router-dom";
import { tracker } from "@utils/openreplay";

interface Value {
  // Signature functions
  signPrescription: (pin: string, requestId: string) => void;

  // PIN states
  pinState: PinState;
  setPinState: (pinState: PinState) => void;

  // Loading states
  isLoading: boolean;
  loadingTitle: string;

  // Error states
  isErrorOnSigningPrescription: boolean;
  errorOnSigningPrescription: Error | null;
  isErrorOnSigningCompoundedPrescription: boolean;
  errorOnSigningCompoundedPrescription: Error | null;

  // Modal states
  showCreationErrorModal: boolean;
  showInvalidPinErrorModal: boolean;
  showBlockedPinErrorModal: boolean;
  showUnknownErrorModal: boolean;
  showValidationRequiredModal: boolean;
  showCertificateRevokedModal: boolean;
  esignErrorMessage: string;

  // Modal handlers
  setShowCreationErrorModal: (show: boolean) => void;
  setShowInvalidPinErrorModal: (show: boolean) => void;
  setShowBlockedPinErrorModal: (show: boolean) => void;
  setShowUnknownErrorModal: (show: boolean) => void;
  setShowValidationRequiredModal: (show: boolean) => void;
  setShowCertificateRevokedModal: (show: boolean) => void;

  // Error handlers
  onInvalidPinError: () => void;
  onBlockedPinError: (errorMessage: string) => void;
  onEsignError: () => void;
  onUnknownError: () => void;
}

const DocumentSignatureContext = createContext<Value | null>(null);

export const useDocumentSignatureContext = () => {
  const value = useContext(DocumentSignatureContext);

  if (!value) {
    throw new Error("DocumentSignatureContext: No value provided");
  }

  return value;
};

export const DocumentSignatureContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { requestId = "" } = useParams();

  // PIN states
  const [pinState, setPinState] = useState<PinState>(defaultPinState);

  // Modal states
  const [showCreationErrorModal, setShowCreationErrorModal] = useState(false);

  const [showInvalidPinErrorModal, setShowInvalidPinErrorModal] = useState(false);

  const [showBlockedPinErrorModal, setShowBlockedPinErrorModal] = useState(false);

  const [showUnknownErrorModal, setShowUnknownErrorModal] = useState(false);
  const [showValidationRequiredModal, setShowValidationRequiredModal] = useState(false);
  const [showCertificateRevokedModal, setShowCertificateRevokedModal] = useState(false);
  const [esignErrorMessage, setEsignErrorMessage] = useState("");

  // Error handlers
  const onCreationError = useCallback(() => {
    setShowCreationErrorModal(true);
    tracker?.event("DocumentSignatureSystemError", {
      requestId,
      errorType: "Creation",
    });
  }, [requestId]);

  const onInvalidPinError = useCallback(() => {
    setShowInvalidPinErrorModal(true);
    tracker?.event("DocumentSignatureUserError", {
      requestId,
      errorType: "InvalidPin",
    });
  }, [requestId]);

  const onBlockedPinError = useCallback(
    (errorMessage: string) => {
      setShowBlockedPinErrorModal(true);
      setEsignErrorMessage(errorMessage);
      tracker?.event("DocumentSignatureUserError", {
        requestId,
        errorType: "BlockedPin",
        errorMessage,
      });
    },
    [requestId],
  );

  const onEsignError = useCallback(() => {
    setShowUnknownErrorModal(true);
    tracker?.event("DocumentSignatureSystemError", {
      requestId,
      errorType: "Esign",
    });
  }, [requestId]);

  const onUnknownError = useCallback(() => {
    setShowUnknownErrorModal(true);
    tracker?.event("DocumentSignatureSystemError", {
      requestId,
      errorType: "Unknown",
    });
  }, [requestId]);

  const onValidationRequiredError = useCallback(() => {
    setShowValidationRequiredModal(true);
    tracker?.event("DocumentSignatureUserError", {
      requestId,
      errorType: "ValidationRequired",
    });
  }, [requestId]);

  const onCertificateRevokedError = useCallback(() => {
    setShowCertificateRevokedModal(true);
    tracker?.event("DocumentSignatureUserError", {
      requestId,
      errorType: "CertificateRevoked",
    });
  }, [requestId]);

  // Use the signature hook only when initialized
  const signatureHookResult = useDocumentSignature({
    onCreationError,
    onInvalidPinError,
    onBlockedPinError,
    onEsignError,
    onUnknownError,
    onValidationRequiredError,
    onCertificateRevokedError,
  });

  // Context value
  const value = useMemo(
    () => ({
      ...signatureHookResult,
      pinState,
      setPinState,
      showCreationErrorModal,
      showInvalidPinErrorModal,
      showBlockedPinErrorModal,
      showUnknownErrorModal,
      showValidationRequiredModal,
      showCertificateRevokedModal,
      esignErrorMessage,
      setShowCreationErrorModal,
      setShowInvalidPinErrorModal,
      setShowBlockedPinErrorModal,
      setShowUnknownErrorModal,
      setShowValidationRequiredModal,
      setShowCertificateRevokedModal,
      onInvalidPinError,
      onBlockedPinError,
      onEsignError,
      onUnknownError,
    }),
    [
      signatureHookResult,
      pinState,
      setPinState,
      showCreationErrorModal,
      showInvalidPinErrorModal,
      showBlockedPinErrorModal,
      showUnknownErrorModal,
      showValidationRequiredModal,
      showCertificateRevokedModal,
      esignErrorMessage,
      setShowCreationErrorModal,
      onInvalidPinError,
      onBlockedPinError,
      onEsignError,
      onUnknownError,
    ],
  );

  return (
    <DocumentSignatureContext.Provider value={value}>
      {children}
    </DocumentSignatureContext.Provider>
  );
};
